// Карточка отметки на снимке: что видел прибор и чем это подтверждено.
//
// Карточка обязана говорить не только «что нашли», но и НАСКОЛЬКО этому можно
// верить. Сведение с АИС по одному месту в часовом допуске эталона почти ничего
// не утверждает (0,55 случайных пар на кандидата), и молчаливая строка «судно
// опознано» выглядела бы там точно так же, как настоящее опознание.

import { formatNumber, useLang, useT } from '../i18n'
import { evidenceRows, type EvidenceRow, type Thresholds } from './evidenceRows'
import { verdictLabelKey } from './verdictStyle'

export interface MarkProps {
  sweep_id: number
  local_id: number
  verdict: string
  length_m: number | null
  width_m: number | null
  area_m2: number | null
  course_deg: number | null
  /** Где именно. Без координат карточку не с чем сверить — ни с АИС, ни с
   *  чужим снимком, ни с картой; «судно 93 × 88 м» без места непроверяемо. */
  lon?: number
  lat?: number
  evidence: Record<string, number | boolean | null>
  ais: {
    matched: boolean
    source: string | null
    matched_on: string | null
    reason: string
    distance_m: number | null
    course_delta_deg: number | null
    random_pairs_expected: number | null
    candidates: number
    mmsi: string | null
  } | null
  sensor?: string
  /** Момент съёмки сцены, ISO. Без него точка читается как «сейчас», тогда как
   *  снимок мог быть сделан пять суток назад. */
  captured_at?: string
  scene_id?: string
  source?: string
  /** Пороги прибора — приезжают ответом ручки. Без них блок улик не рисуется:
   *  выдумывать полосу, по которой судили, нельзя. */
  thresholds?: Thresholds | null
}

/** Координата в градусах с полушарием. Знак читается хуже буквы: «−147,5»
 *  на карте Курил выглядит опечаткой, а «147,545° в.д.» — нет. */
function coord(value: number, positive: string, negative: string): string {
  return `${Math.abs(value).toFixed(5)}° ${value >= 0 ? positive : negative}`
}

/** Строка улики: измерение и полоса, в которую оно должно попасть.
 *
 *  Не сошедшееся красится янтарём, а не прячется: карточка объясняет вердикт
 *  целиком, включая то, что против него. Спрятав несошедшееся, мы сделали бы
 *  из объяснения рекламу. */
/** Сколько знаков держать у каждой улики. Сила следа — сотые (полоса
 *  1,35…3,00 иначе схлопнется в «3 при норме 1–3»), метры и кольца — целые. */
const DIGITS: Record<string, number> = {
  wakeStrength: 2, wakeWidth: 0, wakeRings: 0,
  // Сила радарной линии — десятые: полоса тут односторонняя и начинается с 5,
  // и целые слепили бы «5 при пороге 5» из 5,4 и из 5,0.
  wakeSigma: 1, wakeOffset: 0,
}

function EvidenceLine(
  { row, t, lang }: {
    row: EvidenceRow
    t: (k: string, v?: Record<string, string | number>) => string
    lang: string
  },
) {
  const digits = DIGITS[row.key] ?? 0
  const shown: Record<string, string> = {}
  for (const [k, v] of Object.entries(row.values)) {
    shown[k] = formatNumber(lang as never, v, digits)
  }
  const value = row.key === 'shadow'
    ? t(row.ok ? 'imagery.evidence.shadowAbsent' : 'imagery.evidence.shadowPresent')
    : t(`imagery.evidence.${row.key}Value`, shown)
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-mist">{t(`imagery.evidence.${row.key}`)}</span>
      <span className={`text-[12px] tabular-nums ${row.ok ? 'text-foam' : 'text-buoy'}`}>
        {value}
      </span>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-mist">{label}</span>
      <span className="text-[12px] tabular-nums text-foam">{value}</span>
    </div>
  )
}

/** Момент съёмки в UTC — том же, в каком он напечатан в карточке сцены и в
 *  отчёте прогона. Часовой пояс браузера сдвинул бы его на величину, которую
 *  не с чем сверить. */
function momentUtc(iso: string, lang: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(lang, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hour12: false,
  }).format(d) + ' UTC'
}

export function MarkCard({ data, onClose }: { data: MarkProps; onClose: () => void }) {
  const t = useT()
  const { lang } = useLang()

  // Пустой size — это «короче разрешающей ячейки», а не ноль. Ноль читался
  // бы как измерение, которого никто не делал.
  const rows = evidenceRows(data.evidence as Record<string, unknown>, data.thresholds)

  const size = data.length_m == null || data.width_m == null
    ? t('imagery.card.sizeUnknown')
    : `${Math.round(data.length_m)} × ${Math.round(data.width_m)} ${t('units.m')}`

  return (
    <div className="pointer-events-auto w-[300px] rounded-2xl border border-hull bg-abyss/95 p-3 shadow-console backdrop-blur">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-[13px] font-medium text-foam">
            {t(verdictLabelKey(data.verdict))}
          </div>
          <div className="text-[11px] text-mist">
            {t(`imagery.sensor.${data.sensor ?? 'sar'}`)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="rounded-lg px-2 py-0.5 text-[12px] text-mist hover:text-foam"
        >
          ×
        </button>
      </div>

      <div className="space-y-1 border-t border-hull pt-2">
        <Row label={t('imagery.card.size')} value={size} />
        {data.course_deg != null && (
          <Row label={t('imagery.card.course')} value={`${Math.round(data.course_deg)}°`} />
        )}
        {/* МЕСТО. Карточка без него не сверяется ни с чем: ни с АИС, ни с
            чужим снимком, ни с бумажной картой. Пять знаков после запятой —
            это около метра, то есть точнее пикселя снимка (10 м). */}
        {data.lat != null && data.lon != null && (
          <Row
            label={t('imagery.card.place')}
            value={`${coord(data.lat, t('common.north'), t('common.south'))} ${coord(data.lon, t('common.east'), t('common.west'))}`}
          />
        )}
        {/* СЛЕД: здесь только ПРОБА — докуда прибор смотрел. Сила, ширина и
            число сошедшихся колец стоят ниже, в блоке улик, рядом со своими
            полосами: без полосы число не значит ничего. Проба же не улика, а
            оговорка — прибор мерит медиану вдоль луча фиксированной длины и НЕ
            ищет, где след кончается. */}
        {/* У РАДАРА строка другая: он мерит ОСЬ (0…180°), а не направление —
            движущаяся цель смещена вдоль азимутальной оси съёмки относительно
            своей полосы, и сторона неизвестна. Проба у него двусторонняя,
            поэтому «±1,5 км», а не «3,0 км». */}
        {data.evidence?.has_wake === true
          && typeof data.evidence.wake_axis_deg === 'number' ? (
          <Row
            label={t('imagery.card.wakeAxis')}
            value={t('imagery.card.wakeAxisValue', {
              deg: Math.round(data.evidence.wake_axis_deg as number),
              km: (Number(data.evidence.wake_length_m ?? 0) / 1000).toFixed(1),
            })}
          />
        ) : data.evidence?.has_wake === true ? (
          <Row
            label={t('imagery.card.wake')}
            value={t('imagery.card.wakeValue', {
              km: (Number(data.evidence.wake_length_m ?? 0) / 1000).toFixed(1),
            })}
          />
        ) : null}
        {typeof data.evidence?.contrast_db === 'number' && (
          <Row
            label={t('imagery.card.contrast')}
            value={`${(data.evidence.contrast_db as number).toFixed(1)} ${t('units.db')}`}
          />
        )}
        {/* Время съёмки — не украшение. Точка без него читается как «сейчас»,
            а снимок мог быть сделан неделю назад; у двух картинок одних суток
            это к тому же единственное различие. */}
        {data.captured_at && (
          <Row label={t('imagery.card.captured')} value={momentUtc(data.captured_at, lang)} />
        )}
      </div>

      {/* УЛИКИ с их полосами. «Сила 2,74» само по себе не значит ничего:
          смысл числу даёт полоса, а у следа она двусторонняя — выше потолка
          это не «очень уверенное судно», а соседний предмет в кадре. */}
      {rows.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-hull pt-2">
          <div className="text-[11px] text-mist/70">{t('imagery.card.evidence')}</div>
          {rows.map(row => <EvidenceLine key={row.key} row={row} t={t} lang={lang} />)}
        </div>
      )}

      {/* Сцена названа целиком: по ней снимок находят у поставщика и
          перепроверяют — без имени отметку нечем подтвердить извне. */}
      {data.scene_id && (
        <div className="mt-2 break-all border-t border-hull pt-2 text-[10px] leading-tight text-mist/70">
          {data.scene_id}
        </div>
      )}

      <div className="mt-2 border-t border-hull pt-2">
        {data.ais == null ? (
          // «Не спрашивали» — отдельный ответ. Свернув его в «пары нет», мы
          // объявили бы судно молчащим там, где просто не сверялись.
          <div className="text-[11px] text-mist">{t('imagery.ais.notAsked')}</div>
        ) : data.ais.matched ? (
          <div className="space-y-1">
            <div className="text-[12px] text-foam">{t('imagery.ais.matched')}</div>
            {data.ais.distance_m != null && (
              <Row
                label={t('imagery.ais.distance')}
                value={`${Math.round(data.ais.distance_m)} ${t('units.m')}`}
              />
            )}
            {/* Сила пары называется ВСЛУХ: сведение по одному месту в часовом
                допуске эталона почти ничего не утверждает, и выглядеть оно не
                имеет права так же, как пара, сошедшаяся и по курсу. */}
            <div className="text-[11px] text-mist">
              {data.ais.matched_on === 'place+course'
                ? t('imagery.ais.byPlaceAndCourse')
                : t('imagery.ais.byPlaceOnly')}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="text-[12px] text-mist">
              {t(`imagery.ais.reason.${data.ais.reason || 'no_candidate'}`)}
            </div>
            {data.ais.candidates > 1 && (
              <div className="text-[11px] text-mist">
                {t('imagery.ais.candidates', { count: data.ais.candidates })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
